import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, CircularProgress, GlobalStyles, Stack, Typography } from '@mui/material';
import { filtroComConsulta } from './kmzConsulta';
import KmzMapaControles from './KmzMapaControles';
import { adicaoParaContinuar, collectBounds, continuarAdicao, estaNoTrajeto, featureDoPonto, filtrarForaDoTrecho, linhaDoRotulo, linhasDoAjuste, linhasDoMembro, lngLatDoPonto, marcasDoTrecho, membrosVisiveis, nsmpIdDaFeature, pointKey, representanteDoServico, resumoCurtoDoMembro, rotuloDeMapa, rotuloDoPonto, rotuloDoTipo, tracarEntre, trajetoEfetivo } from './kmzGeo';

export const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

export const SATELLITE_STYLE = {
  version: 8,
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
  sources: {
    'esri-imagery': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    },
  },
  layers: [
    { id: 'esri-imagery', type: 'raster', source: 'esri-imagery' },
  ],
};

const corDaFeature = ['coalesce', ['get', 'corResolvida'], '#808080'];
const larguraDaLinha = [
  'match', ['get', 'tipo'],
  'PROTECAO', 7,
  'EQUIPAMENTO', 7,
  'NA', 7,
  4,
];
const larguraDaMarca = ['coalesce', ['get', 'larguraLinha'], 8];
const FILTRO_MARCA_LINHA = ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]];
const iconeBitmapCache = new Map();

function arquivoDoIcone(url) {
  const partes = String(url || '').split('/');
  return partes[partes.length - 1] || '';
}

function varianteDoIcone(tipo) {
  if (tipo === 'POSTE_MADEIRA') return 'madeira';
  if (tipo === 'POSTE_CONCRETO') return 'concreto';
  if (tipo === 'ROTULO') return 'anel';
  return 'padrao';
}

function corDoSimbolo(properties) {
  if (properties.tipo === 'POSTE_MADEIRA') return properties.corDetectada || '#61210b';
  if (properties.tipo === 'POSTE_CONCRETO' || properties.tipo === 'ROTULO') return '#f8fafc';
  return properties.corDetectada || properties.corResolvida || '#808080';
}

function tamanhoDoIcone(url) {
  const arquivo = arquivoDoIcone(url);
  if (arquivo === 'triangle.png') return 30;
  if (arquivo === 'placemark_square.png') return 14;
  if (arquivo === 'parks.png') return 34;
  return 16;
}

function chaveDoIcone(url, cor, variante) {
  return `${url}|${cor || '#808080'}|${variante}`;
}

const urlDoIcone = ['case',
  ['has', 'iconeUrlResolvida'],
  ['coalesce', ['get', 'iconeUrlResolvida'], ''],
  ['coalesce', ['get', 'iconeUrl'], ''],
];
const temIcone = ['!=', urlDoIcone, ''];
const corDoIcone = ['case',
  ['==', ['get', 'tipo'], 'POSTE_MADEIRA'], ['coalesce', ['get', 'corDetectada'], '#61210b'],
  ['any',
    ['==', ['get', 'tipo'], 'POSTE_CONCRETO'],
    ['==', ['get', 'tipo'], 'ROTULO'],
  ], '#f8fafc',
  ['coalesce', ['get', 'corDetectada'], ['get', 'corResolvida'], '#808080'],
];
const varianteDoIconeExpr = ['match', ['get', 'tipo'],
  'POSTE_MADEIRA', 'madeira',
  'POSTE_CONCRETO', 'concreto',
  'ROTULO', 'anel',
  'padrao',
];

function carregarIcone(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(url));
    image.src = url;
  });
}

function hexParaRgb(cor) {
  const hex = String(cor || '#808080').replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex;
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
}

function pintarIcone(image, cor, tamanho, comBorda) {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0);
  if (comBorda) {
    const imagem = context.getImageData(0, 0, canvas.width, canvas.height);
    const dados = imagem.data;
    const [r, g, b] = hexParaRgb(cor);
    for (let i = 0; i < dados.length; i += 4) {
      if (dados[i + 3] < 16) continue;
      const luz = (dados[i] + dados[i + 1] + dados[i + 2]) / 3;
      if (luz < 150) {
        dados[i] = 0;
        dados[i + 1] = 0;
        dados[i + 2] = 0;
      } else {
        dados[i] = r;
        dados[i + 1] = g;
        dados[i + 2] = b;
      }
    }
    context.putImageData(imagem, 0, 0);
  } else {
    context.globalCompositeOperation = 'source-in';
    context.fillStyle = cor || '#808080';
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  return {
    data: context.getImageData(0, 0, canvas.width, canvas.height),
    pixelRatio: image.width / tamanho,
  };
}

function desenharCirculo(variante, cor) {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  const centro = size / 2;
  context.lineCap = 'round';
  if (variante === 'madeira' || variante === 'concreto') {
    context.beginPath();
    context.arc(centro, centro, 16, 0, Math.PI * 2);
    context.fillStyle = variante === 'madeira' ? (cor || '#61210b') : '#ffffff';
    context.fill();
    context.lineWidth = 5;
    context.strokeStyle = '#000000';
    context.stroke();
    return { data: context.getImageData(0, 0, size, size), pixelRatio: size / 12 };
  }
  context.beginPath();
  context.arc(centro, centro, 14, 0, Math.PI * 2);
  context.lineWidth = 8;
  context.strokeStyle = '#000000';
  context.stroke();
  context.beginPath();
  context.arc(centro, centro, 14, 0, Math.PI * 2);
  context.lineWidth = 4;
  context.strokeStyle = '#ffffff';
  context.stroke();
  return { data: context.getImageData(0, 0, size, size), pixelRatio: size / 10 };
}

async function garantirIcones(map, collection, ativo) {
  const pares = new Map();
  (collection.features || []).forEach((feature) => {
    const properties = feature.properties || {};
    const url = Object.prototype.hasOwnProperty.call(properties, 'iconeUrlResolvida')
      ? properties.iconeUrlResolvida
      : properties.iconeUrl;
    if (!url || !feature.geometry || feature.geometry.type !== 'Point') return;
    const cor = corDoSimbolo(properties);
    const variante = varianteDoIcone(properties.tipo);
    pares.set(chaveDoIcone(url, cor, variante), { url, cor, variante });
  });
  await Promise.all([...pares.entries()].map(async ([id, { url, cor, variante }]) => {
    if (!ativo() || !map.getStyle() || map.hasImage(id)) return;
    let bitmap = iconeBitmapCache.get(id);
    if (!bitmap) {
      if (variante === 'concreto' || variante === 'madeira' || variante === 'anel') {
        bitmap = desenharCirculo(variante, cor);
      } else {
        const image = await carregarIcone(url);
        bitmap = pintarIcone(image, cor, tamanhoDoIcone(url), arquivoDoIcone(url) === 'triangle.png');
      }
      iconeBitmapCache.set(id, bitmap);
    }
    if (!ativo() || !map.getStyle() || map.hasImage(id)) return;
    map.addImage(id, bitmap.data, { pixelRatio: bitmap.pixelRatio });
  }));
}

function namedMarker({ badgeText, nome, background, square }) {
  const root = document.createElement('div');
  root.style.position = 'relative';
  root.style.width = '0';
  root.style.height = '0';
  root.style.cursor = 'pointer';

  const badge = document.createElement('div');
  badge.textContent = badgeText;
  badge.style.position = 'absolute';
  badge.style.left = '0';
  badge.style.top = '0';
  badge.style.transform = 'translate(-50%, -50%)';
  badge.style.minWidth = '22px';
  badge.style.width = badgeText.length > 2 ? 'auto' : '22px';
  badge.style.height = '22px';
  badge.style.padding = badgeText.length > 2 ? '0 5px' : '0';
  badge.style.boxSizing = 'border-box';
  badge.style.borderRadius = square ? '4px' : '50%';
  badge.style.background = background;
  badge.style.color = '#fff';
  badge.style.fontSize = '12px';
  badge.style.fontWeight = '700';
  badge.style.display = 'flex';
  badge.style.alignItems = 'center';
  badge.style.justifyContent = 'center';
  badge.style.border = '2px solid #fff';
  badge.style.boxShadow = '0 1px 4px rgba(0,0,0,.35)';

  const label = document.createElement('div');
  label.textContent = nome;
  label.style.position = 'absolute';
  label.style.left = badgeText.length > 2 ? '24px' : '14px';
  label.style.top = '0';
  label.style.transform = 'translateY(-50%)';
  label.style.background = background;
  label.style.color = '#fff';
  label.style.fontSize = '12px';
  label.style.fontWeight = '700';
  label.style.lineHeight = '18px';
  label.style.padding = '1px 6px';
  label.style.borderRadius = '4px';
  label.style.border = '2px solid #fff';
  label.style.whiteSpace = 'nowrap';
  label.style.boxShadow = '0 1px 4px rgba(0,0,0,.35)';

  root.appendChild(badge);
  root.appendChild(label);
  return root;
}

function textLabel({ nome, focused }) {
  const root = document.createElement('div');
  root.style.position = 'relative';
  root.style.width = '0';
  root.style.height = '0';
  root.style.cursor = 'pointer';
  const label = document.createElement('div');
  label.textContent = nome;
  label.style.position = 'absolute';
  label.style.left = '12px';
  label.style.top = '0';
  label.style.transform = 'translateY(-50%)';
  label.style.color = focused ? '#fb923c' : '#ffffff';
  label.style.fontSize = '13px';
  label.style.fontWeight = '700';
  label.style.lineHeight = '16px';
  label.style.whiteSpace = 'nowrap';
  label.style.textShadow = '0 0 3px #111827, 0 1px 2px #111827, 1px 0 2px #111827, -1px 0 2px #111827, 0 -1px 2px #111827';
  root.appendChild(label);
  return root;
}

function linhaInfo(label, value) {
  const row = document.createElement('div');
  row.style.fontSize = '13px';
  row.style.lineHeight = '1.4';
  row.style.color = '#111827';
  const nome = document.createElement('span');
  nome.textContent = `${label}:`;
  nome.style.fontWeight = '600';
  row.appendChild(nome);
  row.appendChild(document.createTextNode(` ${value}`));
  return row;
}

function cartaoMembro(member) {
  const linhas = linhasDoMembro(member);
  const resumido = linhas.length > 2;
  const box = document.createElement('div');
  box.style.border = '1px solid #e5e7eb';
  box.style.borderRadius = '6px';
  box.style.padding = '8px 10px';
  if (member.principal) box.style.background = '#f3f4f6';
  const head = document.createElement(resumido ? 'button' : 'div');
  if (resumido) {
    head.type = 'button';
    head.style.width = '100%';
    head.style.padding = '0';
    head.style.border = 'none';
    head.style.background = 'transparent';
    head.style.cursor = 'pointer';
    head.style.textAlign = 'left';
    head.style.font = 'inherit';
    head.style.color = 'inherit';
    head.setAttribute('aria-expanded', 'false');
  }
  const titleRow = document.createElement('div');
  titleRow.style.display = 'flex';
  titleRow.style.alignItems = 'center';
  titleRow.style.justifyContent = 'space-between';
  titleRow.style.gap = '8px';
  const nome = document.createElement('div');
  nome.textContent = member.nome || '';
  nome.style.fontWeight = '700';
  nome.style.fontSize = '13px';
  nome.style.color = '#111827';
  titleRow.appendChild(nome);
  const lado = document.createElement('div');
  lado.style.display = 'flex';
  lado.style.alignItems = 'center';
  lado.style.gap = '6px';
  lado.style.flexShrink = '0';
  if (member.principal) {
    const pill = document.createElement('span');
    pill.textContent = 'Principal';
    pill.style.fontSize = '11px';
    pill.style.fontWeight = '600';
    pill.style.lineHeight = '16px';
    pill.style.background = '#111827';
    pill.style.color = '#fff';
    pill.style.borderRadius = '999px';
    pill.style.padding = '0 6px';
    lado.appendChild(pill);
  }
  const chevron = document.createElement('span');
  if (resumido) {
    chevron.textContent = '▾';
    chevron.style.fontSize = '14px';
    chevron.style.lineHeight = '1';
    chevron.style.color = '#4b5563';
    lado.appendChild(chevron);
  }
  if (lado.childNodes.length) titleRow.appendChild(lado);
  head.appendChild(titleRow);
  const tipo = rotuloDoTipo(member);
  if (tipo) {
    const kind = document.createElement('div');
    kind.textContent = tipo;
    kind.style.marginTop = '2px';
    kind.style.fontSize = '12px';
    kind.style.color = '#4b5563';
    head.appendChild(kind);
  }
  const resumo = resumoCurtoDoMembro(member);
  let resumoEl = null;
  if (resumido && resumo) {
    resumoEl = document.createElement('div');
    resumoEl.textContent = resumo;
    resumoEl.style.marginTop = '4px';
    resumoEl.style.fontSize = '12px';
    resumoEl.style.color = '#111827';
    head.appendChild(resumoEl);
  }
  box.appendChild(head);
  if (!resumido) {
    linhas.forEach((linha) => box.appendChild(linhaInfo(linha.label, linha.value)));
    return box;
  }
  const detalhes = document.createElement('div');
  detalhes.hidden = true;
  detalhes.style.marginTop = '6px';
  linhas.forEach((linha) => detalhes.appendChild(linhaInfo(linha.label, linha.value)));
  head.addEventListener('click', (event) => {
    event.stopPropagation();
    const aberto = detalhes.hidden;
    detalhes.hidden = !aberto;
    if (resumoEl) resumoEl.hidden = aberto;
    chevron.textContent = aberto ? '▴' : '▾';
    head.setAttribute('aria-expanded', aberto ? 'true' : 'false');
  });
  box.appendChild(detalhes);
  return box;
}

function linkAbrirServico(nsirId, nsmpId) {
  const abrir = document.createElement('a');
  abrir.href = `/v2/nsir/${encodeURIComponent(nsirId)}?nsmpId=${encodeURIComponent(nsmpId)}`;
  abrir.target = '_blank';
  abrir.rel = 'noopener noreferrer';
  abrir.title = 'Abrir serviço';
  abrir.setAttribute('aria-label', 'Abrir serviço em nova aba');
  abrir.style.display = 'inline-flex';
  abrir.style.alignItems = 'center';
  abrir.style.justifyContent = 'center';
  abrir.style.width = '32px';
  abrir.style.height = '32px';
  abrir.style.borderRadius = '8px';
  abrir.style.color = '#111827';
  abrir.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"/></svg>';
  return abrir;
}

function popupDoServico(feature, fechar, nsirId) {
  const properties = feature.properties || {};
  const root = document.createElement('div');
  root.style.color = '#111827';
  root.style.fontSize = '14px';
  root.style.minWidth = '240px';
  root.style.maxWidth = '320px';
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.justifyContent = 'space-between';
  header.style.gap = '12px';
  const title = document.createElement('div');
  title.textContent = properties.codigo ? `Serviço ${properties.codigo}` : 'Serviço';
  title.style.fontWeight = '700';
  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', 'Fechar');
  close.textContent = '×';
  close.style.width = '32px';
  close.style.height = '32px';
  close.style.margin = '-6px -6px -6px 0';
  close.style.padding = '0';
  close.style.border = 'none';
  close.style.borderRadius = '8px';
  close.style.background = 'transparent';
  close.style.color = '#111827';
  close.style.fontSize = '22px';
  close.style.lineHeight = '32px';
  close.style.cursor = 'pointer';
  close.addEventListener('click', fechar);
  const acoes = document.createElement('div');
  acoes.style.display = 'flex';
  acoes.style.alignItems = 'center';
  acoes.style.flexShrink = '0';
  if (nsirId && properties.nsmpId) acoes.appendChild(linkAbrirServico(nsirId, properties.nsmpId));
  acoes.appendChild(close);
  header.appendChild(title);
  header.appendChild(acoes);
  root.appendChild(header);
  const corpo = document.createElement('div');
  corpo.style.display = 'flex';
  corpo.style.flexDirection = 'column';
  corpo.style.gap = '6px';
  corpo.style.marginTop = '10px';
  if (properties.servico) corpo.appendChild(linhaInfo('Serviço', properties.servico));
  if (properties.qtd) corpo.appendChild(linhaInfo('Quantidade', properties.qtd));
  if (properties.resultadoAuditoria) corpo.appendChild(linhaInfo('Resultado', properties.resultadoAuditoria));
  if (properties.observacao) corpo.appendChild(linhaInfo('Observação', properties.observacao));
  root.appendChild(corpo);
  return root;
}

function popupDoPonto(feature, fechar) {
  const membros = membrosVisiveis(feature);
  const root = document.createElement('div');
  root.style.color = '#111827';
  root.style.fontSize = '14px';
  root.style.minWidth = '240px';
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.justifyContent = 'space-between';
  header.style.gap = '12px';
  const ehLinha = feature.geometry && (feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString');
  const title = document.createElement('div');
  title.textContent = ehLinha ? 'Neste trecho' : 'Neste ponto';
  title.style.fontWeight = '700';
  title.style.fontSize = '14px';
  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', 'Fechar');
  close.textContent = '×';
  close.style.width = '32px';
  close.style.height = '32px';
  close.style.margin = '-6px -6px -6px 0';
  close.style.padding = '0';
  close.style.border = 'none';
  close.style.borderRadius = '8px';
  close.style.background = 'transparent';
  close.style.color = '#111827';
  close.style.fontSize = '22px';
  close.style.lineHeight = '32px';
  close.style.cursor = 'pointer';
  close.style.flexShrink = '0';
  close.addEventListener('click', fechar);
  header.appendChild(title);
  header.appendChild(close);
  root.appendChild(header);
  const count = document.createElement('div');
  if (!membros.length) count.textContent = 'Sem ficha neste símbolo';
  else count.textContent = membros.length === 1 ? '1 equipamento' : `${membros.length} equipamentos`;
  count.style.marginTop = '2px';
  count.style.fontSize = '12px';
  count.style.color = '#4b5563';
  root.appendChild(count);
  const lista = document.createElement('div');
  lista.style.display = 'flex';
  lista.style.flexDirection = 'column';
  lista.style.gap = '6px';
  lista.style.marginTop = '10px';
  lista.style.maxHeight = '320px';
  lista.style.overflowY = 'auto';
  membros.forEach((member) => lista.appendChild(cartaoMembro(member)));
  root.appendChild(lista);
  return root;
}

function pontoDoFeature(feature) {
  const properties = (feature && feature.properties) || {};
  if (!properties.nome) return null;
  return {
    nome: properties.nome,
    nomeReal: properties.nomeReal,
    tipo: properties.tipo,
  };
}

function textoDoPasso(modo, origem) {
  if (modo === 'adicionar' && !origem) return 'Clique um ponto do trajeto atual. É de onde o trecho novo sai.';
  if (modo === 'adicionar') return 'Clique um ponto fora do trajeto. É até onde ele vai.';
  if (modo === 'remover' && !origem) return 'Clique um ponto do trajeto atual. É de onde o pedaço sai.';
  if (modo === 'remover') return 'Clique outro ponto do trajeto. Sai o pedaço entre os dois.';
  return '';
}

function cliqueVale(modo, origem, ponto, noTrajeto) {
  if (!ponto) return false;
  if (!origem) return noTrajeto;
  if (pointKey(ponto) === pointKey(origem.ponto)) return false;
  if (modo === 'adicionar') return !noTrajeto;
  return noTrajeto;
}

const CAMADAS_REDE = ['rede-linhas-casing', 'rede-linhas', 'rede-linhas-hit', 'rede-pontos', 'rede-icones'];
const CAMADAS_MARCAS = ['marcas-linhas-casing', 'marcas-linhas', 'marcas-linhas-hit', 'marcas-circulos', 'marcas-icones', 'marcas-rotulos'];
const CAMADAS_CAMINHO = ['caminho-linhas', 'caminho-linhas-hit'];
const FILTRO_LINHA = ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]];
const FILTRO_PONTO = ['==', ['geometry-type'], 'Point'];
const FILTRO_ROTULO = ['all',
  ['==', ['geometry-type'], 'Point'],
  ['!=', ['coalesce', ['get', 'nome'], ''], ''],
];

function basesDoRecorte(temIconeExpr) {
  const pontoSemIcone = ['all', FILTRO_PONTO, ['!', temIconeExpr], ['!', ['has', 'iconeUrlResolvida']]];
  const pontoComIcone = ['all', FILTRO_PONTO, temIconeExpr];
  return {
    'rede-linhas-casing': { base: FILTRO_LINHA, ponto: false },
    'rede-linhas': { base: FILTRO_LINHA, ponto: false },
    'rede-linhas-hit': { base: FILTRO_LINHA, ponto: false },
    'caminho-linhas-casing': { base: FILTRO_LINHA, ponto: false },
    'caminho-linhas': { base: FILTRO_LINHA, ponto: false },
    'caminho-linhas-hit': { base: FILTRO_LINHA, ponto: false },
    'marcas-linhas-casing': { base: FILTRO_MARCA_LINHA, ponto: false },
    'marcas-linhas': { base: FILTRO_MARCA_LINHA, ponto: false },
    'marcas-linhas-hit': { base: FILTRO_MARCA_LINHA, ponto: false },
    'marcas-circulos': { base: FILTRO_PONTO, ponto: true },
    'marcas-icones': { base: pontoComIcone, ponto: true },
    'marcas-rotulos': { base: FILTRO_ROTULO, ponto: false },
    'rota-linhas': { base: null, ponto: false },
    'rota-pontos': { base: FILTRO_PONTO, ponto: true },
    'rede-pontos': { base: pontoSemIcone, ponto: true },
    'rede-icones': { base: pontoComIcone, ponto: true },
    'rede-rotulos': { base: FILTRO_ROTULO, ponto: false },
    'sugestao-rotulos': { base: FILTRO_ROTULO, ponto: false },
    'sugestao-pontos-circulos': { base: pontoSemIcone, ponto: true },
    'sugestao-icones': { base: pontoComIcone, ponto: true },
  };
}

function aplicarConsultaRecorte(map, consulta, etiquetas) {
  const bases = basesDoRecorte(temIcone);
  Object.keys(bases).forEach((id) => {
    if (!map.getLayer(id)) return;
    const item = bases[id];
    map.setFilter(id, filtroComConsulta(item.base, consulta, item.ponto, etiquetas));
  });
}

function definirVisibilidade(map, ids, visivel) {
  const valor = visivel ? 'visible' : 'none';
  ids.forEach((id) => {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', valor);
  });
}

const KmzRecorteMap = React.forwardRef(({
  rede,
  caminho,
  servicos,
  rota,
  mostrarCru,
  mostrarGerado,
  mostrarRota,
  selectedEnds,
  fitKey,
  active,
  focusedKey,
  equipamentoInicial,
  onSelect,
  fullScreen,
  onToggleFullScreen,
  onTrechosManuais,
  consulta,
  camera,
  nsirId,
  editarTrecho,
}, ref) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const openPopupRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const redeRef = useRef(rede);
  const caminhoRef = useRef(caminho);
  const servicosRef = useRef(servicos);
  const nsirIdRef = useRef(nsirId);
  const fittedKeyRef = useRef(null);
  const iconTokenRef = useRef(0);
  const markersRef = useRef([]);
  const applyOverlayRef = useRef(() => {});
  const basemapModeRef = useRef('satellite');
  const [tilesLoading, setTilesLoading] = useState(true);
  const [basemap, setBasemap] = useState('satellite');
  const [mostrarFora, setMostrarFora] = useState(true);
  const [mostrarInfo, setMostrarInfo] = useState(true);
  const mostrarInfoRef = useRef(true);
  const consultaRef = useRef(consulta);
  mostrarInfoRef.current = mostrarInfo;
  consultaRef.current = consulta;
  const [modoTrecho, setModoTrecho] = useState('nenhum');
  const [origem, setOrigem] = useState(null);
  const [trechos, setTrechos] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const trechosRef = useRef([]);
  const onTrechosManuaisRef = useRef(onTrechosManuais);
  const modoTrechoRef = useRef('nenhum');
  const origemRef = useRef(null);
  const aplicarCliqueRef = useRef(() => false);
  const cursorEdicaoRef = useRef(() => null);
  const feedbackTimerRef = useRef(null);
  const redeDesenhada = useMemo(
    () => (mostrarFora ? rede : filtrarForaDoTrecho(rede, caminho)),
    [mostrarFora, rede, caminho],
  );
  basemapModeRef.current = basemap;
  modoTrechoRef.current = modoTrecho;
  origemRef.current = origem;
  trechosRef.current = trechos;
  onTrechosManuaisRef.current = onTrechosManuais;
  onSelectRef.current = onSelect;
  redeRef.current = rede;
  caminhoRef.current = caminho;
  servicosRef.current = servicos;
  nsirIdRef.current = nsirId;

  const mostrarFeedback = (tom, texto, temporario) => {
    setFeedback({ tom, texto });
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    if (temporario) {
      feedbackTimerRef.current = setTimeout(() => setFeedback(null), 2800);
    }
  };

  aplicarCliqueRef.current = (feature) => {
    const modo = modoTrechoRef.current;
    if (modo === 'nenhum' || nsmpIdDaFeature(feature)) return false;
    if (popupRef.current) popupRef.current.remove();
    const ponto = pontoDoFeature(feature);
    const noTrajeto = feature
      ? estaNoTrajeto(feature, trajetoEfetivo(caminhoRef.current, trechosRef.current, modo))
      : false;
    const origemAtual = origemRef.current;
    if (!ponto) {
      mostrarFeedback('erro', 'Clique em um poste ou equipamento.', true);
      return true;
    }
    if (!origemAtual) {
      if (!noTrajeto) {
        mostrarFeedback('erro', modo === 'adicionar'
          ? 'Esse ponto não está no trajeto. O trecho novo sai de um ponto que já está nele.'
          : 'Esse ponto não está no trajeto. Só dá para remover um pedaço que já está selecionado.', true);
        return true;
      }
      const lngLat = lngLatDoPonto(ponto, feature);
      setOrigem({ ponto, lngLat });
      setFeedback(null);
      return true;
    }
    if (pointKey(ponto) === pointKey(origemAtual.ponto)) {
      mostrarFeedback('erro', 'Escolha outro ponto. O até precisa ser diferente do de.', true);
      return true;
    }
    if (modo === 'adicionar' && noTrajeto) {
      mostrarFeedback('erro', 'Esse ponto já está no trajeto. Escolha um ponto fora, até onde o trecho deve ir.', true);
      return true;
    }
    if (modo === 'remover' && !noTrajeto) {
      mostrarFeedback('erro', 'O segundo ponto também precisa estar no trajeto.', true);
      return true;
    }
    const lngLat = lngLatDoPonto(ponto, feature);
    const colecao = modo === 'adicionar' ? redeRef.current : caminhoRef.current;
    const coordenadas = tracarEntre(colecao, origemAtual.lngLat, lngLat);
    if (!coordenadas) {
      mostrarFeedback('erro', 'Não achei um fio ligando esses dois pontos.', true);
      return true;
    }
    const destino = { ponto, lngLat };
    const continuar = modo === 'adicionar'
      ? adicaoParaContinuar(trechosRef.current, origemAtual.lngLat, caminhoRef.current)
      : null;
    if (continuar) {
      const proximo = continuarAdicao(continuar, origemAtual, destino, coordenadas);
      setTrechos(trechosRef.current.map((item) => (item.id === continuar.id ? proximo : item)));
      setOrigem(null);
      mostrarFeedback('ok', `Continuação até ${rotuloDoPonto(ponto)}. O pedaço segue o mesmo.`);
      return true;
    }
    const item = {
      id: `${modo}-${pointKey(origemAtual.ponto)}-${pointKey(ponto)}-${Date.now()}`,
      tipo: modo,
      de: origemAtual,
      ate: destino,
      coordenadas,
    };
    const lista = trechosRef.current.concat(item);
    setTrechos(lista);
    setOrigem(null);
    mostrarFeedback('ok', modo === 'adicionar'
      ? `Adição de ${rotuloDoPonto(origemAtual.ponto)} até ${rotuloDoPonto(ponto)}. Pode marcar outro pedaço.`
      : `Remoção de ${rotuloDoPonto(origemAtual.ponto)} até ${rotuloDoPonto(ponto)}. Pode marcar outro pedaço.`);
    return true;
  };

  cursorEdicaoRef.current = (feature) => {
    const modo = modoTrechoRef.current;
    if (modo === 'nenhum' || nsmpIdDaFeature(feature)) return null;
    if (!feature) return '';
    const ponto = pontoDoFeature(feature);
    const noTrajeto = estaNoTrajeto(feature, trajetoEfetivo(caminhoRef.current, trechosRef.current, modo));
    return cliqueVale(modo, origemRef.current, ponto, noTrajeto) ? 'pointer' : 'not-allowed';
  };

  const fitCollection = (collection) => {
    const map = mapRef.current;
    if (!map || !collection || !collection.features || !collection.features.length) return;
    const bounds = new maplibregl.LngLatBounds();
    collectBounds(collection, bounds);
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 48, maxZoom: 16 });
    }
  };

  useImperativeHandle(ref, () => ({
    focusPoint(point) {
      const map = mapRef.current;
      if (!map || !point) return;
      const lngLat = lngLatDoPonto(point, featureDoPonto(redeRef.current, point));
      if (!lngLat) return;
      map.flyTo({ center: lngLat, zoom: Math.max(map.getZoom(), 16) });
    },
    fitTrecho() {
      const path = caminhoRef.current;
      const target = path && path.features && path.features.length ? path : redeRef.current;
      fitCollection(target);
    },
    focusTrecho(id) {
      const item = trechosRef.current.find((trecho) => trecho.id === id);
      const linhas = linhasDoAjuste(item);
      if (!linhas.length) return;
      fitCollection({
        type: 'FeatureCollection',
        features: linhas.map((coordinates) => ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates },
        })),
      });
    },
    removerTrecho(id) {
      setTrechos((lista) => lista.filter((trecho) => trecho.id !== id));
    },
  }));

  useEffect(() => {
    const element = containerRef.current;
    if (!element || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: element,
      style: SATELLITE_STYLE,
      center: [-43.35, -21.76],
      zoom: 12,
      maxZoom: 19,
      attributionControl: false,
    });
    mapRef.current = map;
    const onDataLoading = () => setTilesLoading(true);
    const onIdle = () => setTilesLoading(false);
    map.on('dataloading', onDataLoading);
    map.on('idle', onIdle);

    const openPopup = (feature, lngLat) => {
      const properties = feature.properties || {};
      if (properties.nsmpId) {
        if (popupRef.current) popupRef.current.remove();
        const popup = new maplibregl.Popup({ closeButton: false, maxWidth: '360px', className: 'kmz-ponto-popup' })
          .setLngLat(lngLat);
        popup.setDOMContent(popupDoServico(feature, () => popup.remove(), nsirIdRef.current));
        popup.addTo(map);
        popupRef.current = popup;
        return;
      }
      const point = {
        nome: properties.nome,
        nomeReal: properties.nomeReal,
        tipo: properties.tipo,
        chave: properties.chave,
      };
      const buscado = featureDoPonto(redeRef.current, point) || feature;
      const clicouLinha = feature.geometry && feature.geometry.type !== 'Point';
      const redeFeature = clicouLinha && buscado.geometry && buscado.geometry.type === 'Point' ? feature : buscado;
      if (popupRef.current) popupRef.current.remove();
      const popup = new maplibregl.Popup({ closeButton: false, maxWidth: '320px', className: 'kmz-ponto-popup' })
        .setLngLat(lngLat);
      popup.setDOMContent(popupDoPonto(redeFeature, () => popup.remove()));
      popup.addTo(map);
      popupRef.current = popup;
    };
    openPopupRef.current = openPopup;

    let interactionsBound = false;
    const ensureOverlay = () => {
      if (map.getSource('rede')) return;
      const empty = { type: 'FeatureCollection', features: [] };
      map.addSource('rede', { type: 'geojson', data: empty });
      map.addSource('rota', { type: 'geojson', data: empty });
      map.addSource('caminho', { type: 'geojson', data: empty });
      map.addSource('sugestao-pontos', { type: 'geojson', data: empty });
      map.addSource('servicos', { type: 'geojson', data: empty });
      const satellite = basemapModeRef.current === 'satellite';
      map.addLayer({
        id: 'rede-linhas-casing',
        type: 'line',
        source: 'rede',
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        paint: satellite
          ? { 'line-color': '#0f172a', 'line-width': ['+', larguraDaLinha, 3], 'line-opacity': 0.8 }
          : { 'line-color': '#9aa0a6', 'line-width': 0, 'line-opacity': 0 },
      });
      map.addLayer({
        id: 'rede-linhas',
        type: 'line',
        source: 'rede',
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        paint: {
          'line-color': corDaFeature,
          'line-width': larguraDaLinha,
          'line-opacity': 0.95,
        },
      });
      map.addLayer({
        id: 'rota-linhas',
        type: 'line',
        source: 'rota',
        paint: {
          'line-color': '#c026d3',
          'line-width': 3,
          'line-opacity': 0.95,
        },
      });
      map.addLayer({
        id: 'rota-pontos',
        type: 'circle',
        source: 'rota',
        filter: FILTRO_PONTO,
        paint: {
          'circle-radius': 4.5,
          'circle-color': '#c026d3',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'caminho-linhas-casing',
        type: 'line',
        source: 'caminho',
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        layout: { visibility: 'none' },
        paint: satellite
          ? { 'line-color': '#0f172a', 'line-width': ['+', larguraDaLinha, 3], 'line-opacity': 0.8 }
          : { 'line-color': '#9aa0a6', 'line-width': 0, 'line-opacity': 0 },
      });
      map.addLayer({
        id: 'caminho-linhas',
        type: 'line',
        source: 'caminho',
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        paint: {
          'line-color': corDaFeature,
          'line-width': larguraDaLinha,
        },
      });
      map.addSource('marcas', { type: 'geojson', data: empty });
      map.addLayer({
        id: 'marcas-linhas-casing',
        type: 'line',
        source: 'marcas',
        filter: FILTRO_MARCA_LINHA,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0f172a',
          'line-width': ['+', larguraDaMarca, 4],
          'line-opacity': 0.95,
        },
      });
      map.addLayer({
        id: 'marcas-linhas',
        type: 'line',
        source: 'marcas',
        filter: FILTRO_MARCA_LINHA,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': corDaFeature,
          'line-width': larguraDaMarca,
          'line-opacity': 1,
        },
      });
      map.addLayer({
        id: 'marcas-linhas-hit',
        type: 'line',
        source: 'marcas',
        filter: FILTRO_MARCA_LINHA,
        paint: {
          'line-color': corDaFeature,
          'line-width': 18,
          'line-opacity': 0.01,
        },
      });
      map.addLayer({
        id: 'marcas-circulos',
        type: 'circle',
        source: 'marcas',
        filter: FILTRO_PONTO,
        paint: {
          'circle-radius': 7,
          'circle-color': corDaFeature,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'marcas-icones',
        type: 'symbol',
        source: 'marcas',
        filter: ['all', FILTRO_PONTO, temIcone],
        layout: {
          'icon-image': ['concat', urlDoIcone, '|', corDoIcone, '|', varianteDoIconeExpr],
          'icon-size': 1,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'marcas-rotulos',
        type: 'symbol',
        source: 'marcas',
        filter: FILTRO_ROTULO,
        layout: {
          'text-field': ['get', 'nome'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          visibility: mostrarInfoRef.current ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#111827',
          'text-halo-width': 1.4,
        },
      });
      map.addSource('pedacos-manuais', { type: 'geojson', data: empty });
      map.addLayer({
        id: 'pedacos-adicionar-casing',
        type: 'line',
        source: 'pedacos-manuais',
        filter: ['==', ['get', 'tipo'], 'adicionar'],
        paint: { 'line-color': '#831843', 'line-width': 10, 'line-opacity': 0.9 },
      });
      map.addLayer({
        id: 'pedacos-adicionar',
        type: 'line',
        source: 'pedacos-manuais',
        filter: ['==', ['get', 'tipo'], 'adicionar'],
        paint: { 'line-color': '#f472b6', 'line-width': 6, 'line-opacity': 1 },
      });
      map.addLayer({
        id: 'pedacos-remover-casing',
        type: 'line',
        source: 'pedacos-manuais',
        filter: ['==', ['get', 'tipo'], 'remover'],
        paint: { 'line-color': '#450a0a', 'line-width': 10, 'line-opacity': 0.95 },
      });
      map.addLayer({
        id: 'pedacos-remover',
        type: 'line',
        source: 'pedacos-manuais',
        filter: ['==', ['get', 'tipo'], 'remover'],
        paint: { 'line-color': '#ef4444', 'line-width': 6, 'line-opacity': 1 },
      });
      const hitPaint = {
        'line-color': corDaFeature,
        'line-width': 14,
        'line-opacity': 0.01,
      };
      map.addLayer({
        id: 'rede-linhas-hit',
        type: 'line',
        source: 'rede',
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        paint: hitPaint,
      });
      map.addLayer({
        id: 'caminho-linhas-hit',
        type: 'line',
        source: 'caminho',
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        paint: hitPaint,
      });
      map.addLayer({
        id: 'rede-pontos',
        type: 'circle',
        source: 'rede',
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          ['!', temIcone],
          ['!', ['has', 'iconeUrlResolvida']],
        ],
        paint: {
          'circle-radius': ['match', ['get', 'tipo'], 'PROTECAO', 6, 'NA', 6, 'EQUIPAMENTO', 6, 3.5],
          'circle-color': corDaFeature,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'rede-icones',
        type: 'symbol',
        source: 'rede',
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          temIcone,
        ],
        layout: {
          'icon-image': ['concat', urlDoIcone, '|', corDoIcone, '|', varianteDoIconeExpr],
          'icon-size': 1,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'rede-rotulos',
        type: 'symbol',
        source: 'rede',
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          ['!=', ['coalesce', ['get', 'nome'], ''], ''],
        ],
        layout: {
          'text-field': ['get', 'nome'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          visibility: mostrarInfoRef.current ? 'visible' : 'none',
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#111827',
          'text-halo-width': 1.4,
        },
      });
      map.addLayer({
        id: 'sugestao-rotulos',
        type: 'symbol',
        source: 'sugestao-pontos',
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          ['!=', ['coalesce', ['get', 'nome'], ''], ''],
        ],
        layout: {
          'text-field': ['get', 'nome'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.6],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          visibility: 'none',
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#111827',
          'text-halo-width': 1.4,
        },
      });
      map.addLayer({
        id: 'sugestao-pontos-circulos',
        type: 'circle',
        source: 'sugestao-pontos',
        layout: { visibility: 'none' },
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          ['!', temIcone],
          ['!', ['has', 'iconeUrlResolvida']],
        ],
        paint: {
          'circle-radius': ['match', ['get', 'tipo'], 'PROTECAO', 6, 'NA', 6, 'EQUIPAMENTO', 6, 3.5],
          'circle-color': corDaFeature,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'sugestao-icones',
        type: 'symbol',
        source: 'sugestao-pontos',
        layout: {
          visibility: 'none',
          'icon-image': ['concat', urlDoIcone, '|', corDoIcone, '|', varianteDoIconeExpr],
          'icon-size': 1,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          temIcone,
        ],
      });
      map.addLayer({
        id: 'servicos-linhas-casing',
        type: 'line',
        source: 'servicos',
        filter: FILTRO_LINHA,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0f172a',
          'line-width': 9,
          'line-opacity': 0.85,
        },
      });
      map.addLayer({
        id: 'servicos-linhas',
        type: 'line',
        source: 'servicos',
        filter: FILTRO_LINHA,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['coalesce', ['get', 'corResolvida'], '#188038'],
          'line-width': 5,
          'line-opacity': 1,
        },
      });
      map.addLayer({
        id: 'servicos-linhas-hit',
        type: 'line',
        source: 'servicos',
        filter: FILTRO_LINHA,
        paint: {
          'line-color': '#000000',
          'line-width': 16,
          'line-opacity': 0.01,
        },
      });
      map.addLayer({
        id: 'servicos-circulos',
        type: 'circle',
        source: 'servicos',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 7,
          'circle-color': ['coalesce', ['get', 'corResolvida'], '#188038'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'servicos-icones',
        type: 'symbol',
        source: 'servicos',
        filter: ['all',
          ['==', ['geometry-type'], 'Point'],
          temIcone,
        ],
        layout: {
          'icon-image': ['concat', urlDoIcone, '|', corDoIcone, '|', varianteDoIconeExpr],
          'icon-size': 1,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      map.addLayer({
        id: 'servicos-rotulos',
        type: 'symbol',
        source: 'servicos',
        filter: ['all',
          ['==', ['get', 'rotular'], true],
          ['!=', ['coalesce', ['get', 'codigo'], ''], ''],
        ],
        layout: {
          'text-field': ['get', 'codigo'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.9],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#111827',
          'text-halo-width': 1.4,
        },
      });
      if (interactionsBound) return;
      interactionsBound = true;
      const camadas = ['servicos-icones', 'servicos-circulos', 'servicos-rotulos', 'servicos-linhas-hit', 'marcas-icones', 'marcas-circulos', 'marcas-rotulos', 'sugestao-icones', 'sugestao-pontos-circulos', 'rede-icones', 'rede-pontos', 'marcas-linhas-hit', 'caminho-linhas-hit', 'rede-linhas-hit'];
      const hitsNoPonto = (point) => {
        const layers = camadas.filter((id) => map.getLayer(id));
        if (!layers.length) return [];
        return map.queryRenderedFeatures(point, { layers });
      };
      const featureDoClique = (point) => {
        const hits = hitsNoPonto(point);
        const ponto = hits.find((item) => item.layer && (
          item.layer.id === 'servicos-icones'
          || item.layer.id === 'servicos-circulos'
          || item.layer.id === 'servicos-rotulos'
          || item.layer.id === 'rede-icones'
          || item.layer.id === 'rede-pontos'
          || item.layer.id === 'marcas-icones'
          || item.layer.id === 'marcas-circulos'
          || item.layer.id === 'marcas-rotulos'
          || item.layer.id === 'sugestao-icones'
          || item.layer.id === 'sugestao-pontos-circulos'
        ));
        const servicoLinha = hits.find((item) => item.layer && item.layer.id === 'servicos-linhas-hit');
        const linhaHit = hits.find((item) => item.layer && (
          item.layer.id === 'marcas-linhas-hit'
          || item.layer.id === 'caminho-linhas-hit'
          || item.layer.id === 'rede-linhas-hit'
        ));
        let feature = ponto || servicoLinha || linhaHit;
        if (ponto && (ponto.properties || {}).tipo === 'ROTULO') {
          const linha = linhaDoRotulo([caminhoRef.current, redeRef.current], ponto);
          if (linha) feature = linha;
        }
        if (!feature) return null;
        const colecao = servicosRef.current;
        return representanteDoServico(colecao && colecao.features, feature);
      };
      map.on('click', (event) => {
        const feature = featureDoClique(event.point);
        if (!feature) {
          if (modoTrechoRef.current !== 'nenhum') aplicarCliqueRef.current(null);
          return;
        }
        if (aplicarCliqueRef.current(feature)) return;
        openPopup(feature, event.lngLat);
      });
      map.on('mousemove', (event) => {
        const feature = featureDoClique(event.point);
        const cursorEdicao = cursorEdicaoRef.current(feature);
        if (cursorEdicao != null) {
          map.getCanvas().style.cursor = cursorEdicao;
          return;
        }
        map.getCanvas().style.cursor = feature ? 'pointer' : '';
      });
    };

    map.on('style.load', () => {
      ensureOverlay();
      applyOverlayRef.current();
    });

    const observer = new ResizeObserver(() => {
      if (element.clientWidth > 0 && element.clientHeight > 0) map.resize();
    });
    observer.observe(element);
    requestAnimationFrame(() => {
      if (element.clientWidth > 0 && element.clientHeight > 0) map.resize();
    });

    return () => {
      observer.disconnect();
      map.off('dataloading', onDataLoading);
      map.off('idle', onIdle);
      if (popupRef.current) popupRef.current.remove();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return undefined;
    const currentSatellite = map.getSource('esri-imagery');
    const wantSatellite = basemap === 'satellite';
    if (Boolean(currentSatellite) === wantSatellite) return undefined;
    map.setStyle(wantSatellite ? SATELLITE_STYLE : STYLE_URL, { diff: false });
    return undefined;
  }, [basemap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('rede-rotulos')) return undefined;
    map.setLayoutProperty('rede-rotulos', 'visibility', mostrarInfo && mostrarCru ? 'visible' : 'none');
    if (map.getLayer('sugestao-rotulos')) {
      map.setLayoutProperty('sugestao-rotulos', 'visibility', mostrarInfo && mostrarGerado && !mostrarCru ? 'visible' : 'none');
    }
    if (map.getLayer('marcas-rotulos')) {
      map.setLayoutProperty('marcas-rotulos', 'visibility', mostrarInfo && (mostrarCru || mostrarGerado) ? 'visible' : 'none');
    }
    return undefined;
  }, [mostrarInfo, mostrarCru, mostrarGerado]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const apply = () => {
      if (!map.getSource('rede')) return;
      map.getSource('rede').setData(redeDesenhada);
      map.getSource('caminho').setData(caminho);
      if (map.getSource('rota')) {
        map.getSource('rota').setData(rota && rota.features ? rota : { type: 'FeatureCollection', features: [] });
      }
      definirVisibilidade(map, CAMADAS_REDE, mostrarCru);
      definirVisibilidade(map, CAMADAS_CAMINHO, mostrarGerado);
      const marcas = marcasDoTrecho(rede, caminho);
      if (map.getSource('marcas')) map.getSource('marcas').setData(marcas);
      definirVisibilidade(map, CAMADAS_MARCAS, Boolean(mostrarCru || mostrarGerado));
      if (map.getLayer('marcas-rotulos')) {
        map.setLayoutProperty('marcas-rotulos', 'visibility', mostrarInfo && (mostrarCru || mostrarGerado) ? 'visible' : 'none');
      }
      if (map.getLayer('rota-linhas')) {
        const rotaVazia = !rota || !rota.features || !rota.features.length;
        const rotaVisivel = mostrarRota && !rotaVazia ? 'visible' : 'none';
        map.setLayoutProperty('rota-linhas', 'visibility', rotaVisivel);
        if (map.getLayer('rota-pontos')) map.setLayoutProperty('rota-pontos', 'visibility', rotaVisivel);
      }
      aplicarConsultaRecorte(map, consultaRef.current, mostrarInfoRef.current);
      const pontosDaSugestao = filtrarForaDoTrecho(rede, caminho);
      if (map.getSource('sugestao-pontos')) {
        map.getSource('sugestao-pontos').setData(pontosDaSugestao);
      }
      const colecaoServicos = servicos && servicos.features ? servicos : { type: 'FeatureCollection', features: [] };
      if (map.getSource('servicos')) map.getSource('servicos').setData(colecaoServicos);
      const iconesDaSugestao = mostrarGerado && !mostrarCru;
      definirVisibilidade(map, ['sugestao-icones', 'sugestao-pontos-circulos'], iconesDaSugestao);
      if (map.getLayer('rede-rotulos')) {
        map.setLayoutProperty('rede-rotulos', 'visibility', mostrarInfo && mostrarCru ? 'visible' : 'none');
      }
      if (map.getLayer('sugestao-rotulos')) {
        map.setLayoutProperty('sugestao-rotulos', 'visibility', mostrarInfo && mostrarGerado && !mostrarCru ? 'visible' : 'none');
      }
      if (map.getSource('pedacos-manuais')) {
        map.getSource('pedacos-manuais').setData({
          type: 'FeatureCollection',
          features: trechos.flatMap((item) => linhasDoAjuste(item).map((coordinates) => ({
            type: 'Feature',
            properties: { tipo: item.tipo, id: item.id },
            geometry: { type: 'LineString', coordinates },
          }))),
        });
      }
      if (map.getLayer('caminho-linhas-casing')) {
        const caminhoVazio = !caminho.features || !caminho.features.length;
        map.setLayoutProperty('caminho-linhas-casing', 'visibility', !mostrarGerado || mostrarFora || caminhoVazio ? 'none' : 'visible');
      }
      const iconToken = iconTokenRef.current + 1;
      iconTokenRef.current = iconToken;
      garantirIcones(map, redeDesenhada, () => iconToken === iconTokenRef.current)
        .then(() => garantirIcones(map, pontosDaSugestao, () => iconToken === iconTokenRef.current))
        .then(() => garantirIcones(map, marcas, () => iconToken === iconTokenRef.current))
        .then(() => garantirIcones(map, colecaoServicos, () => iconToken === iconTokenRef.current))
        .catch(() => {});
      markersRef.current.forEach((marker) => marker.remove());
      const inicioKey = equipamentoInicial && equipamentoInicial.encontrado
        ? pointKey(equipamentoInicial)
        : '';
      const markers = selectedEnds.map((end) => {
        const feature = featureDoPonto(rede, end);
        const lngLat = lngLatDoPonto(end, feature);
        if (!Array.isArray(lngLat) || !Number.isFinite(lngLat[0]) || !Number.isFinite(lngLat[1])) return null;
        const selected = pointKey(end) === focusedKey;
        const element = textLabel({
          nome: rotuloDeMapa(rede, end, lngLat),
          focused: selected,
        });
        element.addEventListener('click', (event) => {
          event.stopPropagation();
          const alvo = feature || { properties: { nome: end.nome, nomeReal: end.nomeReal, tipo: end.tipo }, geometry: { type: 'Point', coordinates: lngLat } };
          if (aplicarCliqueRef.current(alvo)) return;
          if (onSelectRef.current) onSelectRef.current(end);
          if (openPopupRef.current) openPopupRef.current(alvo, lngLat);
        });
        return new maplibregl.Marker({ element, anchor: 'center' })
          .setLngLat(lngLat)
          .addTo(map);
      }).filter(Boolean);
      const inicioLngLat = lngLatDoPonto(equipamentoInicial, featureDoPonto(rede, equipamentoInicial));
      const inicioJaEFinal = inicioKey && selectedEnds.some((end) => pointKey(end) === inicioKey);
      if (inicioLngLat && equipamentoInicial && equipamentoInicial.nome && !inicioJaEFinal) {
        const element = namedMarker({
          badgeText: 'I',
          nome: equipamentoInicial.nome,
          background: '#047857',
          square: true,
        });
        element.addEventListener('click', (event) => {
          event.stopPropagation();
          const alvo = featureDoPonto(rede, equipamentoInicial) || {
            properties: equipamentoInicial,
            geometry: { type: 'Point', coordinates: inicioLngLat },
          };
          aplicarCliqueRef.current(alvo);
        });
        markers.push(
          new maplibregl.Marker({ element, anchor: 'center' })
            .setLngLat(inicioLngLat)
            .addTo(map)
        );
      }
      if (origem && Array.isArray(origem.lngLat)) {
        const element = namedMarker({
          badgeText: 'De',
          nome: rotuloDoPonto(origem.ponto),
          background: '#c2410c',
          square: true,
        });
        element.addEventListener('click', (event) => event.stopPropagation());
        markers.push(
          new maplibregl.Marker({ element, anchor: 'center' })
            .setLngLat(origem.lngLat)
            .addTo(map)
        );
      }
      markersRef.current = markers;
      const target = caminho.features.length > 0 ? caminho : rede;
      if (fittedKeyRef.current !== fitKey && target.features.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        collectBounds(target, bounds);
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 0 });
          fittedKeyRef.current = fitKey;
        }
      }
    };
    applyOverlayRef.current = apply;
    if (map.getSource('rede')) apply();
    else map.once('style.load', apply);
    return undefined;
  }, [rede, redeDesenhada, caminho, servicos, rota, mostrarCru, mostrarGerado, mostrarRota, mostrarInfo, mostrarFora, selectedEnds, fitKey, focusedKey, equipamentoInicial, origem, trechos]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('rede-linhas')) return undefined;
    aplicarConsultaRecorte(map, consulta, mostrarInfo);
    return undefined;
  }, [consulta, mostrarInfo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !camera || !camera.bounds) return undefined;
    if (camera.pontoUnico) {
      map.easeTo({ center: camera.bounds[0], zoom: 16, bearing: 0, pitch: 0 });
      return undefined;
    }
    map.fitBounds(camera.bounds, { padding: 48, maxZoom: 17 });
    return undefined;
  }, [camera]);

  useEffect(() => {
    if (onTrechosManuaisRef.current) onTrechosManuaisRef.current(trechos);
  }, [trechos]);

  useEffect(() => {
    if (modoTrecho === 'nenhum') return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setModoTrecho('nenhum');
      setOrigem(null);
      setFeedback(null);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [modoTrecho]);

  useEffect(() => {
    if (!active) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;
    let interno = 0;
    const externo = requestAnimationFrame(() => {
      interno = requestAnimationFrame(() => {
        if (!mapRef.current) return;
        map.resize();
        const path = caminhoRef.current;
        const pontos = servicosRef.current;
        const bounds = new maplibregl.LngLatBounds();
        if (path && path.features && path.features.length) collectBounds(path, bounds);
        if (pontos && pontos.features && pontos.features.length) collectBounds(pontos, bounds);
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 64, maxZoom: 17, duration: 0 });
        }
      });
    });
    return () => {
      cancelAnimationFrame(externo);
      if (interno) cancelAnimationFrame(interno);
    };
  }, [active]);

  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      <GlobalStyles
        styles={{
          '.kmz-ponto-popup .maplibregl-popup-content': {
            padding: '12px 12px 14px',
            borderRadius: 8,
          },
        }}
      />
      <Box
        ref={containerRef}
        sx={{ width: '100%', height: '100%' }}
      />
      <KmzMapaControles
        edicao={editarTrecho}
        modo={modoTrecho}
        texto={feedback ? feedback.texto : textoDoPasso(modoTrecho, origem)}
        tom={feedback ? feedback.tom : 'passo'}
        onModo={(proximo) => {
          setModoTrecho((atual) => (atual === proximo ? 'nenhum' : proximo));
          setOrigem(null);
          setFeedback(null);
          if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        }}
        onCancelar={() => {
          setModoTrecho('nenhum');
          setOrigem(null);
          setFeedback(null);
          if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        }}
        mostrarTudo={mostrarFora}
        onMostrarTudo={() => {
          if (popupRef.current) popupRef.current.remove();
          setMostrarFora(true);
        }}
        onMostrarSelecionado={() => {
          if (popupRef.current) popupRef.current.remove();
          setMostrarFora(false);
        }}
        basemap={basemap}
        onBasemap={setBasemap}
        fullScreen={fullScreen}
        onToggleFullScreen={onToggleFullScreen}
        mostrarInfo={mostrarInfo}
        onMostrarInfo={() => setMostrarInfo((atual) => !atual)}
        onCentralizar={() => {
          const path = caminhoRef.current;
          const target = path && path.features && path.features.length ? path : redeRef.current;
          fitCollection(target);
        }}
        onZoomIn={() => {
          const map = mapRef.current;
          if (map) map.zoomIn();
        }}
        onZoomOut={() => {
          const map = mapRef.current;
          if (map) map.zoomOut();
        }}
      />
      {tilesLoading && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 28,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
            boxShadow: 1,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption">Carregando o mapa</Typography>
        </Stack>
      )}
    </Box>
  );
});

KmzRecorteMap.displayName = 'KmzRecorteMap';

KmzRecorteMap.propTypes = {
  rede: PropTypes.object.isRequired,
  caminho: PropTypes.object.isRequired,
  servicos: PropTypes.object,
  rota: PropTypes.object,
  mostrarCru: PropTypes.bool,
  mostrarGerado: PropTypes.bool,
  mostrarRota: PropTypes.bool,
  selectedEnds: PropTypes.arrayOf(PropTypes.object).isRequired,
  fitKey: PropTypes.string,
  active: PropTypes.bool,
  focusedKey: PropTypes.string,
  equipamentoInicial: PropTypes.object,
  onSelect: PropTypes.func,
  fullScreen: PropTypes.bool,
  onToggleFullScreen: PropTypes.func,
  onTrechosManuais: PropTypes.func,
  consulta: PropTypes.object,
  camera: PropTypes.object,
  nsirId: PropTypes.string,
  editarTrecho: PropTypes.bool,
};

KmzRecorteMap.defaultProps = {
  servicos: null,
  rota: null,
  mostrarCru: true,
  mostrarGerado: true,
  mostrarRota: true,
  fitKey: '',
  active: true,
  focusedKey: '',
  equipamentoInicial: null,
  onSelect: undefined,
  fullScreen: false,
  onToggleFullScreen: undefined,
  onTrechosManuais: undefined,
  consulta: null,
  camera: null,
  nsirId: '',
  editarTrecho: true,
};

export default KmzRecorteMap;
